import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Play, Trash2, Music, Lock, Globe, Share2, Repeat, Pause, X } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { usePlayer } from '../context/PlayerContext';
import { API_BASE as API } from '../lib/apiBase';


export default function PlaylistsPage() {
  const { t, lang } = useLang();
  const { user, token } = useAuth();
  const { playSong, currentSong, isPlaying, togglePlay, queue } = usePlayer();
  const [searchParams] = useSearchParams();
  const [playlists, setPlaylists] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [playlistSongs, setPlaylistSongs] = useState([]);
  const [form, setForm] = useState({ name: '', description: '', is_public: true });
  const [allSongs, setAllSongs] = useState([]);
  const [showAddSong, setShowAddSong] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isLooping, setIsLooping] = useState(false);
  const [deepLinked, setDeepLinked] = useState(false);

  const fetchPlaylists = () => {
    const url = token ? `${API}/api/playlists?user_only=true` : `${API}/api/playlists`;
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    fetch(url, { headers })
      .then(r => r.json())
      .then(data => { setPlaylists(data.playlists || []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { fetchPlaylists(); }, [token]);

  useEffect(() => {
    if (deepLinked || loading) return;
    const linkedId = searchParams.get('id');
    if (linkedId) {
      const match = playlists.find(p => p.id === linkedId);
      if (match) { loadPlaylist(match); setDeepLinked(true); }
      else {
        fetch(`${API}/api/playlists/${linkedId}`)
          .then(r => r.ok ? r.json() : null)
          .then(data => { if (data) { setSelectedPlaylist(data); setPlaylistSongs(data.song_details || []); } })
          .catch(() => {});
        setDeepLinked(true);
      }
    }
  }, [searchParams, playlists, loading, deepLinked]);

  const loadPlaylist = async (pl) => {
    setSelectedPlaylist(pl);
    const res = await fetch(`${API}/api/playlists/${pl.id}`);
    const data = await res.json();
    setPlaylistSongs(data.song_details || []);
  };

  const createPlaylist = async (e) => {
    e.preventDefault();
    if (!token) return;
    await fetch(`${API}/api/playlists`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(form)
    });
    setShowCreate(false);
    setForm({ name: '', description: '', is_public: true });
    fetchPlaylists();
  };

  const addSong = async (songId) => {
    if (!selectedPlaylist || !token) return;
    await fetch(`${API}/api/playlists/${selectedPlaylist.id}/add-song`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ song_id: songId })
    });
    loadPlaylist(selectedPlaylist);
    setShowAddSong(false);
  };

  const removeSong = async (songId) => {
    if (!selectedPlaylist || !token) return;
    await fetch(`${API}/api/playlists/${selectedPlaylist.id}/remove-song`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ song_id: songId })
    });
    loadPlaylist(selectedPlaylist);
  };

  const deletePlaylist = async (plId) => {
    if (!token) return;
    await fetch(`${API}/api/playlists/${plId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` }
    });
    setSelectedPlaylist(null);
    fetchPlaylists();
  };

  const openAddSong = async () => {
    const res = await fetch(`${API}/api/songs?limit=50`);
    const data = await res.json();
    setAllSongs(data.songs || []);
    setShowAddSong(true);
  };

  const playAll = () => {
    if (playlistSongs.length === 0) return;
    playSong(playlistSongs[0], playlistSongs, 0);
  };

  const sharePlaylist = async () => {
    if (!selectedPlaylist) return;
    const url = `${window.location.origin}/playlists?id=${selectedPlaylist.id}`;
    const text = `${selectedPlaylist.name} - Playlist en PulseMusic`;
    if (navigator.share) {
      try { await navigator.share({ title: text, text, url }); } catch {}
    } else {
      await navigator.clipboard.writeText(`${text}\n${url}`);
      alert(lang === 'es' ? 'Link copiado!' : 'Link copied!');
    }
  };

  const isCurrentPlaylist = playlistSongs.some(s => s.id === currentSong?.id);

  return (
    <div data-testid="playlists-page" className="min-h-screen pt-14 pb-32">
      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading font-bold text-2xl">{t('playlist.title')}</h1>
            <p className="text-xs text-pulse-muted mt-1">{lang === 'es' ? 'Crea carpetas y escucha en bucle' : 'Create folders and listen on loop'}</p>
          </div>
          {user && (
            <button data-testid="create-playlist-btn" onClick={() => setShowCreate(!showCreate)} className="flex items-center gap-2 bg-pulse-primary text-black px-4 py-2 rounded-xl text-sm font-semibold hover:bg-pulse-primary-hover transition-colors duration-200">
              <Plus size={16} /> {t('playlist.create')}
            </button>
          )}
        </div>

        {showCreate && (
          <form onSubmit={createPlaylist} className="p-5 bg-pulse-surface border border-pulse-border rounded-xl mb-6 space-y-3 animate-fade-in-up">
            <h3 className="font-heading font-semibold text-sm mb-2">{lang === 'es' ? 'Nueva carpeta' : 'New folder'}</h3>
            <input data-testid="playlist-name-input" type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder={t('playlist.name')} className="w-full bg-pulse-elevated border border-pulse-border rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none" required />
            <input type="text" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder={t('playlist.description')} className="w-full bg-pulse-elevated border border-pulse-border rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none" />
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.is_public} onChange={e => setForm(f => ({ ...f, is_public: e.target.checked }))} className="accent-pulse-primary" />
                <Globe size={14} /> {t('playlist.public')}
              </label>
            </div>
            <button type="submit" data-testid="save-playlist-btn" className="bg-pulse-primary text-black px-6 py-2 rounded-lg text-sm font-semibold">{t('common.save')}</button>
          </form>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Playlist sidebar */}
          <div className="md:col-span-1 space-y-2">
            {loading ? [1, 2, 3].map(i => <div key={i} className="shimmer-loading h-16 rounded-xl" />) : playlists.length === 0 ? (
              <div className="text-center py-12">
                <Music size={32} className="text-pulse-muted mx-auto mb-3 opacity-40" />
                <p className="text-pulse-muted text-sm">{t('playlist.empty')}</p>
                {user && <p className="text-xs text-pulse-muted mt-1">{lang === 'es' ? 'Crea tu primera carpeta' : 'Create your first folder'}</p>}
              </div>
            ) : playlists.map(pl => (
              <button key={pl.id} data-testid={`playlist-${pl.id}`} onClick={() => loadPlaylist(pl)} className={`w-full text-left p-4 rounded-xl border transition-all duration-200 ${selectedPlaylist?.id === pl.id ? 'bg-pulse-elevated border-cyan-500/30' : 'bg-pulse-surface border-pulse-border hover:border-cyan-500/20'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-pulse-primary/30 to-purple-500/30 flex items-center justify-center">
                      <Music size={16} className="text-pulse-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">{pl.name}</p>
                      <p className="text-xs text-pulse-muted">{pl.songs?.length || 0} {lang === 'es' ? 'canciones' : 'songs'}</p>
                    </div>
                  </div>
                  {pl.is_public ? <Globe size={12} className="text-pulse-muted" /> : <Lock size={12} className="text-pulse-muted" />}
                </div>
              </button>
            ))}
          </div>

          {/* Playlist content */}
          <div className="md:col-span-2">
            {selectedPlaylist ? (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="font-heading font-bold text-xl">{selectedPlaylist.name}</h2>
                    {selectedPlaylist.description && <p className="text-xs text-pulse-muted mt-1">{selectedPlaylist.description}</p>}
                    <p className="text-[10px] text-pulse-muted mt-0.5">{playlistSongs.length} {lang === 'es' ? 'canciones' : 'songs'}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {playlistSongs.length > 0 && (
                      <>
                        <button data-testid="play-all-btn" onClick={playAll} className="flex items-center gap-1.5 bg-pulse-primary text-black px-4 py-2 rounded-xl text-sm font-bold hover:bg-pulse-primary-hover transition-colors">
                          {isCurrentPlaylist && isPlaying ? <Pause size={14} /> : <Play size={14} />}
                          {isCurrentPlaylist && isPlaying ? 'Pause' : (lang === 'es' ? 'Reproducir' : 'Play All')}
                        </button>
                        <button data-testid="share-playlist-btn" onClick={sharePlaylist} className="p-2 text-pulse-muted hover:text-pulse-primary transition-colors border border-pulse-border rounded-lg" title={lang === 'es' ? 'Compartir' : 'Share'}>
                          <Share2 size={16} />
                        </button>
                      </>
                    )}
                    <button data-testid="add-song-btn" onClick={openAddSong} className="flex items-center gap-1 text-xs bg-pulse-surface border border-pulse-border px-3 py-2 rounded-lg hover:border-cyan-500/30 transition-colors">
                      <Plus size={12} /> {t('playlist.add_song')}
                    </button>
                    <button data-testid="delete-playlist-btn" onClick={() => deletePlaylist(selectedPlaylist.id)} className="text-red-400 hover:text-red-300 p-2">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>

                {playlistSongs.length === 0 ? (
                  <div className="text-center py-16">
                    <Music size={40} className="text-pulse-muted mx-auto mb-3 opacity-30" />
                    <p className="text-pulse-muted text-sm">{lang === 'es' ? 'Carpeta vacia. Anade canciones!' : 'Empty folder. Add songs!'}</p>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {playlistSongs.map((song, idx) => {
                      const title = lang === 'en' ? (song.title_en || song.title_es) : song.title_es;
                      const isCurrent = currentSong?.id === song.id;
                      return (
                        <div key={song.id} className={`flex items-center gap-3 p-2.5 rounded-xl transition-all duration-200 group ${isCurrent ? 'bg-pulse-primary/10 border border-pulse-primary/20' : 'hover:bg-pulse-elevated'}`}>
                          <span className="text-pulse-muted text-xs w-5 text-right font-mono">{idx + 1}</span>
                          <button onClick={() => playSong(song, playlistSongs, idx)} className="flex items-center gap-3 flex-1 min-w-0 text-left">
                            <div className="w-10 h-10 rounded-lg overflow-hidden relative flex-shrink-0">
                              <img src={song.cover} alt={title} className="w-full h-full object-cover" />
                              {isCurrent && isPlaying && <div className="absolute inset-0 bg-black/40 flex items-center justify-center"><div className="flex gap-0.5">{[1,2,3].map(i => <div key={i} className="w-0.5 bg-pulse-primary animate-pulse" style={{height: `${8+i*3}px`, animationDelay: `${i*0.15}s`}} />)}</div></div>}
                            </div>
                            <div className="min-w-0">
                              <p className={`text-sm truncate ${isCurrent ? 'text-pulse-primary font-semibold' : 'font-medium'}`}>{title}</p>
                              <p className="text-xs text-pulse-muted">{song.artist_name}</p>
                            </div>
                          </button>
                          <div className="flex items-center gap-1">
                            <span className="text-[10px] text-pulse-muted">{song.plays ? `${(song.plays/1000).toFixed(0)}K` : ''}</span>
                            <button onClick={() => removeSong(song.id)} className="text-pulse-muted hover:text-red-400 p-1 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={14} /></button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-64 text-pulse-muted">
                <Music size={40} className="mb-3 opacity-30" />
                <p className="text-sm">{lang === 'es' ? 'Selecciona una carpeta' : 'Select a folder'}</p>
              </div>
            )}
          </div>
        </div>

        {/* Add Song Modal */}
        {showAddSong && (
          <div className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowAddSong(false)}>
            <div className="bg-pulse-surface border border-pulse-border rounded-2xl w-full max-w-lg max-h-[70vh] overflow-y-auto p-5" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading font-bold text-lg">{t('playlist.add_song')}</h3>
                <button onClick={() => setShowAddSong(false)} className="text-pulse-muted hover:text-white"><X size={20} /></button>
              </div>
              <div className="space-y-1">
                {allSongs.map(song => {
                  const title = lang === 'en' ? (song.title_en || song.title_es) : song.title_es;
                  const alreadyAdded = playlistSongs.some(s => s.id === song.id);
                  return (
                    <button key={song.id} onClick={() => !alreadyAdded && addSong(song.id)} disabled={alreadyAdded} className={`w-full flex items-center gap-3 p-2.5 rounded-xl transition-colors text-left ${alreadyAdded ? 'opacity-40' : 'hover:bg-pulse-elevated'}`}>
                      <img src={song.cover} alt={title} className="w-10 h-10 rounded-lg object-cover" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm truncate">{title}</p>
                        <p className="text-xs text-pulse-muted">{song.artist_name}</p>
                      </div>
                      {alreadyAdded ? <span className="text-[10px] text-pulse-muted">{lang === 'es' ? 'Agregada' : 'Added'}</span> : <Plus size={14} className="text-pulse-primary" />}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
